import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function SponsorBriefs() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [briefs, setBriefs] = useState([]);
  const load = async () => {
    const { data } = await api.get("/api/briefs/mine");
    setBriefs(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/api/briefs/${id}`);
    toast.success(t("sponsor_briefs.toast_deleted"));
    load();
  };

  return (
    <DashboardLayout
      title={t("sponsor_briefs.title")}
      subtitle={t("sponsor_briefs.subtitle")}
      actions={
        <Button onClick={() => nav("/dashboard/briefs/nuevo")} data-testid="button-new-brief" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
          <Plus size={16} className="mr-2" /> {t("sponsor_briefs.new_brief")}
        </Button>
      }
    >
      {briefs.length === 0 ? (
        <div className="kpi-card p-8 text-center">
          <p className="text-slate-500">{t("sponsor_briefs.empty")}</p>
          <Button onClick={() => nav("/dashboard/briefs/nuevo")} className="mt-4 bg-petroleum text-white hover:bg-petroleum/90 rounded-full">{t("sponsor_briefs.create_brief")}</Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {briefs.map((b) => (
            <div key={b.id} className="kpi-card p-5" data-testid={`row-brief-${b.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-2xl text-petroleum">{b.title}</h3>
                    {b.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("sponsor_briefs.badge_active")}</Badge> : <Badge variant="secondary">{t("sponsor_briefs.badge_inactive")}</Badge>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{b.description}</p>
                  <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4">
                    {b.budget_min || b.budget_max ? <span>{t("sponsor_briefs.budget_line", { currency: b.currency, min: (b.budget_min?.toLocaleString() || 0), max: (b.budget_max?.toLocaleString() || t("sponsor_briefs.budget_open")) })}</span> : null}
                    {(b.contribution_types || []).length ? <span>{t("sponsor_briefs.contributions_line", { list: b.contribution_types.join(", ") })}</span> : null}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Link to={`/dashboard/briefs/${b.id}`} className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-1" data-testid={`link-edit-brief-${b.id}`}>
                    <Edit3 size={12} /> {t("sponsor_briefs.edit")}
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid={`button-delete-brief-${b.id}`}>
                        <Trash2 size={12} /> {t("sponsor_briefs.delete")}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("sponsor_briefs.confirm_delete_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("sponsor_briefs.confirm_delete_desc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(b.id)}>{t("sponsor_briefs.delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
